/**
 * 補助科目マスタ専用メンテナンス画面
 * 勘定科目に紐づく補助科目のみを一覧表示し、追加・編集・削除（有効/無効）を行う。
 */
import React, { useState, useEffect, useCallback } from 'react';
import { getErrorMessage } from '../utils/errorHandler';

interface HojoItem {
  id: number;
  kamoku_id: number;
  kamoku_name: string | null;
  hojo_code: string;
  hojo_name: string;
  shiyou_kubun: boolean;
  tenant_id?: number | null;
}

interface KamokuOption {
  id: number;
  kamoku_code: string;
  kamoku: string;
  hojo_kamoku_ari?: boolean;
}

interface HojoKamokuManagementProps {
  onMenuSelect: (menuId: string) => void;
  tenantName: string;
  currentUser: any;
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  currentTenantId: number | null;
}

const HojoKamokuManagement: React.FC<HojoKamokuManagementProps> = ({
  tenantName,
  currentUser,
  apiFetch,
  currentTenantId,
}) => {
  const [hojoList, setHojoList] = useState<HojoItem[]>([]);
  const [kamokuOptions, setKamokuOptions] = useState<KamokuOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterKamokuId, setFilterKamokuId] = useState<number | ''>('');
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingHojo, setEditingHojo] = useState<HojoItem | null>(null);
  const [modalKamokuId, setModalKamokuId] = useState<number | ''>('');
  const [modalCode, setModalCode] = useState('');
  const [modalName, setModalName] = useState('');
  const [modalActive, setModalActive] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchHojoList = useCallback(async () => {
    if (currentTenantId == null) {
      setHojoList([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      params.set('tenant_id', String(currentTenantId));
      if (includeDisabled) params.set('include_disabled', 'true');
      const response = await apiFetch(`/api/hojo-kamoku?${params.toString()}`);
      if (!response.ok) throw new Error(await getErrorMessage(response, '補助科目の取得に失敗しました'));
      const data = await response.json();
      setHojoList(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.message || '補助科目の取得に失敗しました');
      setHojoList([]);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, currentTenantId, includeDisabled]);

  const fetchKamokuOptions = useCallback(async () => {
    if (currentTenantId == null) return;
    try {
      const response = await apiFetch(`/api/kamoku`);
      if (!response.ok) return;
      const data = await response.json();
      setKamokuOptions(Array.isArray(data) ? data : []);
    } catch {
      setKamokuOptions([]);
    }
  }, [apiFetch, currentTenantId]);

  useEffect(() => {
    fetchHojoList();
  }, [fetchHojoList]);

  useEffect(() => {
    fetchKamokuOptions();
  }, [fetchKamokuOptions]);

  const filteredList = hojoList.filter((h) => {
    const searchLower = searchTerm.toLowerCase();
    const matchSearch =
      !searchTerm ||
      String(h.hojo_code || '').toLowerCase().includes(searchLower) ||
      String(h.hojo_name || '').toLowerCase().includes(searchLower) ||
      String(h.kamoku_name || '').toLowerCase().includes(searchLower);
    const matchKamoku = !filterKamokuId || h.kamoku_id === filterKamokuId;
    return matchSearch && matchKamoku;
  });

  const openNewModal = () => {
    setEditingHojo(null);
    setModalKamokuId(kamokuOptions.length ? kamokuOptions[0].id : '');
    setModalCode('');
    setModalName('');
    setModalActive(true);
    setShowModal(true);
  };

  const openEditModal = (hojo: HojoItem) => {
    setEditingHojo(hojo);
    setModalKamokuId(hojo.kamoku_id);
    setModalCode(hojo.hojo_code);
    setModalName(hojo.hojo_name);
    setModalActive(hojo.shiyou_kubun);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingHojo(null);
  };

  // 一覧のチェックボックスで有効/無効をトグル（勘定科目マスタと同じ方式）
  const handleToggleHojoActive = async (h: HojoItem) => {
    try {
      setError(null);
      const response = await apiFetch(`/api/hojo-kamoku/${h.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shiyou_kubun: !h.shiyou_kubun }),
      });
      if (!response.ok) throw new Error(await getErrorMessage(response, '状態の更新に失敗しました'));
      setHojoList(prev => prev.map(item => (item.id === h.id ? { ...item, shiyou_kubun: !item.shiyou_kubun } : item)));
    } catch (err: any) {
      setError(err?.message || '状態の更新に失敗しました');
    }
  };

  const handleSave = async () => {
    if (!modalCode.trim() || !modalName.trim()) {
      alert('補助科目コードと補助科目名は必須です');
      return;
    }
    if (editingHojo) {
      if (!editingHojo.id) return;
      try {
        setSaving(true);
        const response = await apiFetch(`/api/hojo-kamoku/${editingHojo.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            hojo_code: modalCode.trim(),
            hojo_name: modalName.trim(),
            shiyou_kubun: modalActive,
          }),
        });
        if (!response.ok) throw new Error(await getErrorMessage(response, '更新に失敗しました'));
        await fetchHojoList();
        closeModal();
      } catch (err: any) {
        alert(err?.message || '更新に失敗しました');
      } finally {
        setSaving(false);
      }
    } else {
      if (modalKamokuId === '') {
        alert('勘定科目を選択してください');
        return;
      }
      try {
        setSaving(true);
        const response = await apiFetch('/api/hojo-kamoku/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            kamoku_id: modalKamokuId,
            hojo_code: modalCode.trim(),
            hojo_name: modalName.trim(),
            shiyou_kubun: modalActive,
            tenant_id: currentTenantId,
          }),
        });
        if (!response.ok) throw new Error(await getErrorMessage(response, '登録に失敗しました'));
        await fetchHojoList();
        closeModal();
      } catch (err: any) {
        alert(err?.message || '登録に失敗しました');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleDelete = async (hojo: HojoItem) => {
    if (!window.confirm(`補助科目「${hojo.hojo_name}」を無効にしますか？\n（削除後も有効を含む一覧で表示できます）`)) return;
    try {
      setLoading(true);
      const response = await apiFetch(`/api/hojo-kamoku/${hojo.id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(await getErrorMessage(response, '無効化に失敗しました'));
      await fetchHojoList();
    } catch (err: any) {
      alert(err?.message || '無効化に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const kamokuWithHojo = kamokuOptions.filter((k) => k.hojo_kamoku_ari);

  return (
    <div
      className="hojo-kamoku-management"
      style={{
        backgroundColor: '#f0f0f0',
        minHeight: '100vh',
        fontFamily: "'MS UI Gothic', 'Hiragino Kaku Gothic ProN', sans-serif",
        fontSize: '11px',
      }}
    >


      <div style={{ padding: '15px' }}>
        {error && (
          <div
            style={{
              backgroundColor: '#ffe6e6',
              color: '#cc0000',
              padding: '8px',
              marginBottom: '10px',
              border: '1px solid #cc0000',
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '12px', alignItems: 'center' }}>
          <label>
            <span style={{ marginRight: '6px' }}>勘定科目で絞り込み:</span>
            <select
              value={filterKamokuId === '' ? '' : filterKamokuId}
              onChange={(e) => setFilterKamokuId(e.target.value === '' ? '' : Number(e.target.value))}
              style={{ padding: '4px 8px', fontSize: '11px', minWidth: '200px' }}
            >
              <option value="">すべて</option>
              {kamokuOptions.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.kamoku_code} {k.kamoku}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span style={{ marginRight: '6px' }}>検索:</span>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="コード・補助科目名・勘定科目名"
              style={{ padding: '4px 8px', fontSize: '11px', width: '180px' }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <input
              type="checkbox"
              checked={includeDisabled}
              onChange={(e) => setIncludeDisabled(e.target.checked)}
            />
            無効を含む
          </label>
          <button
            type="button"
            onClick={openNewModal}
            style={{
              padding: '6px 14px',
              backgroundColor: '#2e7d32',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '12px',
            }}
          >
            新規追加
          </button>
        </div>

        <div style={{ overflow: 'auto', border: '2px inset #e0e0e0', backgroundColor: '#fff' }}>
          {loading ? (
            <div style={{ padding: '24px', textAlign: 'center' }}>読み込み中...</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
              <thead>
                <tr style={{ backgroundColor: '#e0e0e0' }}>
                  <th style={{ border: '1px solid #999', padding: '6px', width: '22%' }}>勘定科目（親）</th>
                  <th style={{ border: '1px solid #999', padding: '6px', width: '12%' }}>補助科目コード</th>
                  <th style={{ border: '1px solid #999', padding: '6px', width: '28%' }}>補助科目名</th>
                  <th style={{ border: '1px solid #999', padding: '6px', width: '8%' }}>有効</th>
                  <th style={{ border: '1px solid #999', padding: '6px', width: '18%' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map((h) => (
                  <tr key={h.id} style={{ backgroundColor: h.shiyou_kubun ? '#fff' : '#f5f5f5' }}>
                    <td style={{ border: '1px solid #ccc', padding: '6px' }}>{h.kamoku_name ?? '-'}</td>
                    <td style={{ border: '1px solid #ccc', padding: '6px' }}>{h.hojo_code}</td>
                    <td style={{ border: '1px solid #ccc', padding: '6px' }}>{h.hojo_name}</td>
                    <td style={{ border: '1px solid #ccc', padding: '6px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={h.shiyou_kubun}
                        onChange={(e) => { e.stopPropagation(); handleToggleHojoActive(h); }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td style={{ border: '1px solid #ccc', padding: '6px' }}>
                      <button
                        type="button"
                        onClick={() => openEditModal(h)}
                        style={{
                          marginRight: '6px',
                          padding: '2px 8px',
                          fontSize: '11px',
                          cursor: 'pointer',
                        }}
                      >
                        編集
                      </button>
                      {h.shiyou_kubun && (
                        <button
                          type="button"
                          onClick={() => handleDelete(h)}
                          style={{
                            padding: '2px 8px',
                            fontSize: '11px',
                            cursor: 'pointer',
                            color: '#c62828',
                          }}
                        >
                          無効化
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!loading && filteredList.length === 0 && (
            <div style={{ padding: '24px', textAlign: 'center', color: '#666' }}>
              補助科目がありません。「新規追加」で登録するか、勘定科目マスタで「補助科目あり」を付けた科目に補助科目を登録してください。
            </div>
          )}
        </div>

        <div style={{ marginTop: '8px', fontSize: '10px', color: '#666' }}>
          表示: {filteredList.length} 件 {includeDisabled && '(無効を含む)'}
        </div>
      </div>

      {showModal && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={closeModal}
        >
          <div
            style={{
              backgroundColor: '#fff',
              padding: '20px',
              borderRadius: '8px',
              minWidth: '360px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '16px' }}>
              {editingHojo ? '補助科目の編集' : '補助科目の新規登録'}
            </h3>
            {!editingHojo && (
              <div style={{ marginBottom: '10px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>勘定科目（親）</label>
                <select
                  value={modalKamokuId === '' ? '' : modalKamokuId}
                  onChange={(e) => setModalKamokuId(e.target.value === '' ? '' : Number(e.target.value))}
                  style={{ width: '100%', padding: '6px', fontSize: '12px' }}
                >
                  {kamokuWithHojo.length > 0 ? (
                    kamokuWithHojo.map((k) => (
                      <option key={k.id} value={k.id}>
                        {k.kamoku_code} {k.kamoku}
                      </option>
                    ))
                  ) : (
                    kamokuOptions.map((k) => (
                      <option key={k.id} value={k.id}>
                        {k.kamoku_code} {k.kamoku}
                      </option>
                    ))
                  )}
                </select>
              </div>
            )}
            {editingHojo && (
              <div style={{ marginBottom: '10px', fontSize: '12px', color: '#666' }}>
                勘定科目: {editingHojo.kamoku_name ?? '-'}
              </div>
            )}
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>補助科目コード *</label>
              <input
                type="text"
                value={modalCode}
                onChange={(e) => setModalCode(e.target.value)}
                disabled={!!editingHojo}
                style={{ width: '100%', padding: '6px', fontSize: '12px', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>補助科目名 *</label>
              <input
                type="text"
                value={modalName}
                onChange={(e) => setModalName(e.target.value)}
                style={{ width: '100%', padding: '6px', fontSize: '12px', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                <input
                  type="checkbox"
                  checked={modalActive}
                  onChange={(e) => setModalActive(e.target.checked)}
                />
                有効
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button type="button" onClick={closeModal} style={{ padding: '6px 14px', cursor: 'pointer' }}>
                キャンセル
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '6px 14px',
                  backgroundColor: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}
              >
                {saving ? '保存中...' : editingHojo ? '更新' : '登録'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HojoKamokuManagement;
